export const TASK_COMMAND_OUTBOX_DATABASE = "oyako-flow-task-command-outbox";

const DATABASE_VERSION = 1;
const MUTATION_STORE = "task-command-mutations";
const RECEIPT_STORE = "task-command-receipts";
const DEFAULT_LEASE_DURATION_MS = 30_000;
const DEFAULT_MAX_RETRY_ATTEMPTS = 5;

const TASK_COMMANDS = [
  "start",
  "complete",
  "reopen",
  "request_help",
  "clear_help",
  "verify",
  "return",
  "defer",
  "skip",
  "cancel",
  "restore",
  "reorder",
  "checklist_complete",
  "checklist_reopen",
] as const;
const HELP_REASONS = ["stuck", "need_explanation", "need_material", "need_time", "other"] as const;
const OUTBOX_STATES = ["pending", "sending", "conflict", "failed"] as const;

export type TaskCommandOperation = (typeof TASK_COMMANDS)[number];
export type TaskLifecycleOperation = Exclude<
  TaskCommandOperation,
  `checklist_${string}` | "reorder"
>;
export type TaskChecklistOperation = Extract<TaskCommandOperation, `checklist_${string}`>;
export type TaskReorderOperation = Extract<TaskCommandOperation, "reorder">;
export type TaskHelpReason = (typeof HELP_REASONS)[number];
export type TaskCommandOutboxState = (typeof OUTBOX_STATES)[number];
export type TaskCommandActorType = "adult" | "child";

export interface TaskCommandPayload {
  assignment_id: string;
  expected_version: number;
  checklist_item_id?: string;
  reason?: TaskHelpReason;
  note?: string;
  local_date?: string;
  ordered_items?: { task_id: string; assignment_id: string; expected_version: number }[];
}

export interface TaskCommandActorBinding {
  actorId: string;
  actorType: TaskCommandActorType;
  sessionId: string;
  deviceId: string;
}

export interface TaskCommandOutboxErrorState {
  code: string;
  message: string;
  retryable: boolean;
  recordedAt: number;
}

export interface TaskCommandConflictState {
  code: string;
  serverVersion: number | null;
  recordedAt: number;
}

export interface TaskCommandMutation {
  schemaVersion: 1;
  mutationId: string;
  idempotencyKey: string;
  actorBinding: TaskCommandActorBinding;
  workspaceId: string;
  entityId: string;
  operation: TaskCommandOperation;
  expectedVersion: number;
  payload: TaskCommandPayload;
  dependencyIds: string[];
  state: TaskCommandOutboxState;
  attemptCount: number;
  retryAttemptCount: number;
  availableAt: number;
  claimId: string | null;
  claimExpiresAt: number | null;
  lastError: TaskCommandOutboxErrorState | null;
  conflict: TaskCommandConflictState | null;
  createdAt: number;
  updatedAt: number;
}

export interface TaskCommandSuccessReceipt {
  schemaVersion: 1;
  mutationId: string;
  idempotencyKey: string;
  workspaceId: string;
  acknowledgedAt: number;
}

export interface EnqueueTaskCommandInput {
  mutationId?: string;
  actorBinding: TaskCommandActorBinding;
  workspaceId: string;
  entityId: string;
  operation: TaskCommandOperation;
  payload: TaskCommandPayload;
  dependencyIds?: string[];
  createdAt?: number;
}

export interface TaskCommandOutboxOptions {
  databaseName?: string;
  indexedDB?: IDBFactory;
  leaseDurationMs?: number;
  maxRetryAttempts?: number;
  now?: () => number;
  randomUUID?: () => string;
}

export class TaskCommandOutboxError extends Error {
  readonly code:
    | "INDEXEDDB_UNAVAILABLE"
    | "OUTBOX_OPEN_FAILED"
    | "OUTBOX_ROW_INVALID"
    | "OUTBOX_INPUT_INVALID"
    | "OUTBOX_MUTATION_CONFLICT"
    | "OUTBOX_CLAIM_LOST";

  constructor(code: TaskCommandOutboxError["code"], message: string) {
    super(message);
    this.name = "TaskCommandOutboxError";
    this.code = code;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asError(value: unknown): Error {
  return value instanceof Error ? value : new Error("An unknown IndexedDB error occurred.");
}

function isIndexedDbFactory(value: unknown): value is IDBFactory {
  return isRecord(value) && typeof value.open === "function";
}

function isNonEmptyString(value: unknown, maximum = 2048): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= maximum;
}

function isInteger(value: unknown, minimum = 0): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= minimum;
}

function isNullableInteger(value: unknown): value is number | null {
  return value === null || isInteger(value);
}

function isNullableString(value: unknown): value is string | null {
  return value === null || isNonEmptyString(value);
}

function includesLiteral<const T extends readonly string[]>(
  values: T,
  value: unknown,
): value is T[number] {
  return typeof value === "string" && (values as readonly string[]).includes(value);
}

function isActorBinding(value: unknown): value is TaskCommandActorBinding {
  return (
    isRecord(value) &&
    isNonEmptyString(value.actorId, 512) &&
    (value.actorType === "adult" || value.actorType === "child") &&
    isNonEmptyString(value.sessionId, 512) &&
    isNonEmptyString(value.deviceId, 512)
  );
}

function isTaskCommandPayload(
  value: unknown,
  operation: TaskCommandOperation,
): value is TaskCommandPayload {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.assignment_id, 512) ||
    !isInteger(value.expected_version, 1)
  ) {
    return false;
  }
  const isChecklist = operation === "checklist_complete" || operation === "checklist_reopen";
  if (isChecklist !== isNonEmptyString(value.checklist_item_id, 512)) return false;
  if (operation === "reorder") {
    if (
      typeof value.local_date !== "string" ||
      !/^\d{4}-\d{2}-\d{2}$/u.test(value.local_date) ||
      !Array.isArray(value.ordered_items) ||
      value.ordered_items.length < 2 ||
      value.ordered_items.length > 50 ||
      !value.ordered_items.every(
        (item) =>
          isRecord(item) &&
          isNonEmptyString(item.task_id, 512) &&
          isNonEmptyString(item.assignment_id, 512) &&
          isInteger(item.expected_version, 1),
      ) ||
      new Set(value.ordered_items.map((item) => (item as Record<string, unknown>).task_id)).size !==
        value.ordered_items.length ||
      new Set(value.ordered_items.map((item) => (item as Record<string, unknown>).assignment_id))
        .size !== value.ordered_items.length ||
      value.reason !== undefined ||
      value.note !== undefined
    ) {
      return false;
    }
  } else if (value.local_date !== undefined || value.ordered_items !== undefined) {
    return false;
  }
  if (value.reason !== undefined && !includesLiteral(HELP_REASONS, value.reason)) return false;
  if (value.note !== undefined && (typeof value.note !== "string" || value.note.length > 1000)) {
    return false;
  }
  return true;
}

function isErrorState(value: unknown): value is TaskCommandOutboxErrorState | null {
  return (
    value === null ||
    (isRecord(value) &&
      isNonEmptyString(value.code, 200) &&
      typeof value.message === "string" &&
      value.message.length <= 1000 &&
      typeof value.retryable === "boolean" &&
      isInteger(value.recordedAt))
  );
}

function isConflictState(value: unknown): value is TaskCommandConflictState | null {
  return (
    value === null ||
    (isRecord(value) &&
      isNonEmptyString(value.code, 200) &&
      (value.serverVersion === null || isInteger(value.serverVersion, 1)) &&
      isInteger(value.recordedAt))
  );
}

function hasValidClaimState(value: Record<string, unknown>): boolean {
  if (value.state === "sending") {
    return (
      isNonEmptyString(value.claimId, 512) &&
      isInteger(value.claimExpiresAt) &&
      isInteger(value.updatedAt) &&
      value.claimExpiresAt >= value.updatedAt
    );
  }
  return value.claimId === null && value.claimExpiresAt === null;
}

export function decodeTaskCommandMutation(value: unknown): TaskCommandMutation {
  if (
    !isRecord(value) ||
    value.schemaVersion !== 1 ||
    !isNonEmptyString(value.mutationId, 512) ||
    !isNonEmptyString(value.idempotencyKey, 200) ||
    value.idempotencyKey.length < 8 ||
    !isActorBinding(value.actorBinding) ||
    !isNonEmptyString(value.workspaceId, 512) ||
    !isNonEmptyString(value.entityId, 512) ||
    !includesLiteral(TASK_COMMANDS, value.operation) ||
    !isInteger(value.expectedVersion, 1) ||
    !isTaskCommandPayload(value.payload, value.operation) ||
    value.expectedVersion !== value.payload.expected_version ||
    !Array.isArray(value.dependencyIds) ||
    !value.dependencyIds.every((dependencyId) => isNonEmptyString(dependencyId, 512)) ||
    new Set(value.dependencyIds).size !== value.dependencyIds.length ||
    value.dependencyIds.includes(value.mutationId) ||
    !includesLiteral(OUTBOX_STATES, value.state) ||
    !isInteger(value.attemptCount) ||
    !isInteger(value.retryAttemptCount) ||
    value.retryAttemptCount > value.attemptCount ||
    !isInteger(value.availableAt) ||
    !isNullableString(value.claimId) ||
    !isNullableInteger(value.claimExpiresAt) ||
    !hasValidClaimState(value) ||
    !isErrorState(value.lastError) ||
    !isConflictState(value.conflict) ||
    (value.state === "conflict") !== (value.conflict !== null) ||
    ((value.state === "conflict" || value.state === "failed") && value.lastError === null) ||
    !isInteger(value.createdAt) ||
    !isInteger(value.updatedAt) ||
    value.updatedAt < value.createdAt
  ) {
    throw new TaskCommandOutboxError("OUTBOX_ROW_INVALID", "A stored task mutation is invalid.");
  }
  return value as unknown as TaskCommandMutation;
}

function decodeSuccessReceipt(value: unknown): TaskCommandSuccessReceipt {
  if (
    !isRecord(value) ||
    value.schemaVersion !== 1 ||
    !isNonEmptyString(value.mutationId, 512) ||
    !isNonEmptyString(value.idempotencyKey, 200) ||
    !isNonEmptyString(value.workspaceId, 512) ||
    !isInteger(value.acknowledgedAt)
  ) {
    throw new TaskCommandOutboxError("OUTBOX_ROW_INVALID", "A stored outbox receipt is invalid.");
  }
  return value as unknown as TaskCommandSuccessReceipt;
}

function assertPositiveInteger(value: number, label: string): void {
  if (!Number.isInteger(value) || value <= 0) {
    throw new TaskCommandOutboxError(
      "OUTBOX_INPUT_INVALID",
      `${label} must be a positive integer.`,
    );
  }
}

function assertClaimOwner(mutation: TaskCommandMutation, claimId: string): void {
  if (mutation.state !== "sending" || mutation.claimId !== claimId) {
    throw new TaskCommandOutboxError(
      "OUTBOX_CLAIM_LOST",
      "The task mutation is no longer owned by this sender claim.",
    );
  }
}

function clonePayload(payload: TaskCommandPayload): TaskCommandPayload {
  return {
    assignment_id: payload.assignment_id,
    expected_version: payload.expected_version,
    ...(payload.checklist_item_id === undefined
      ? {}
      : { checklist_item_id: payload.checklist_item_id }),
    ...(payload.reason === undefined ? {} : { reason: payload.reason }),
    ...(payload.note === undefined ? {} : { note: payload.note }),
    ...(payload.local_date === undefined ? {} : { local_date: payload.local_date }),
    ...(payload.ordered_items === undefined
      ? {}
      : { ordered_items: payload.ordered_items.map((item) => ({ ...item })) }),
  };
}

export function createTaskCommandMutation(
  input: EnqueueTaskCommandInput,
  options: { now: number; randomUUID: () => string },
): TaskCommandMutation {
  const mutationId = input.mutationId ?? options.randomUUID();
  const idempotencyKey = `task-command:${mutationId}`;
  const dependencyIds = [...new Set(input.dependencyIds ?? [])].sort();
  const createdAt = input.createdAt ?? options.now;
  const candidate: TaskCommandMutation = {
    schemaVersion: 1,
    mutationId,
    idempotencyKey,
    actorBinding: { ...input.actorBinding },
    workspaceId: input.workspaceId,
    entityId: input.entityId,
    operation: input.operation,
    expectedVersion: input.payload.expected_version,
    payload: clonePayload(input.payload),
    dependencyIds,
    state: "pending",
    attemptCount: 0,
    retryAttemptCount: 0,
    availableAt: createdAt,
    claimId: null,
    claimExpiresAt: null,
    lastError: null,
    conflict: null,
    createdAt,
    updatedAt: createdAt,
  };
  try {
    return decodeTaskCommandMutation(candidate);
  } catch (error) {
    if (error instanceof TaskCommandOutboxError) {
      throw new TaskCommandOutboxError("OUTBOX_INPUT_INVALID", error.message);
    }
    throw error;
  }
}

function mutationIntent(mutation: TaskCommandMutation): string {
  return JSON.stringify([
    mutation.mutationId,
    mutation.idempotencyKey,
    mutation.actorBinding.actorId,
    mutation.actorBinding.actorType,
    mutation.actorBinding.sessionId,
    mutation.actorBinding.deviceId,
    mutation.workspaceId,
    mutation.entityId,
    mutation.operation,
    mutation.expectedVersion,
    mutation.payload,
    mutation.dependencyIds,
  ]);
}

export function hasSameTaskCommandIntent(
  first: TaskCommandMutation,
  second: TaskCommandMutation,
): boolean {
  return mutationIntent(first) === mutationIntent(second);
}

export function taskCommandMatchesScope(
  mutation: TaskCommandMutation,
  scope: { workspaceId: string; actorBinding: TaskCommandActorBinding },
): boolean {
  return (
    mutation.workspaceId === scope.workspaceId &&
    mutation.actorBinding.actorId === scope.actorBinding.actorId &&
    mutation.actorBinding.actorType === scope.actorBinding.actorType &&
    mutation.actorBinding.deviceId === scope.actorBinding.deviceId &&
    (scope.actorBinding.actorType === "child" ||
      mutation.actorBinding.sessionId === scope.actorBinding.sessionId)
  );
}

export function selectNextTaskCommandMutation(
  mutations: readonly TaskCommandMutation[],
  acknowledgedMutationIds: ReadonlySet<string>,
  now: number,
): TaskCommandMutation | null {
  const eligible = mutations.filter((mutation) => {
    const leaseAvailable =
      (mutation.state === "pending" && mutation.availableAt <= now) ||
      (mutation.state === "sending" &&
        mutation.claimExpiresAt !== null &&
        mutation.claimExpiresAt <= now);
    return (
      leaseAvailable &&
      mutation.dependencyIds.every((dependencyId) => acknowledgedMutationIds.has(dependencyId))
    );
  });
  eligible.sort(
    (first, second) =>
      first.createdAt - second.createdAt || first.mutationId.localeCompare(second.mutationId),
  );
  return eligible[0] ?? null;
}

export function claimTaskCommandMutation(
  mutation: TaskCommandMutation,
  input: { claimId: string; now: number; leaseDurationMs: number },
): TaskCommandMutation {
  assertPositiveInteger(input.leaseDurationMs, "leaseDurationMs");
  const claimable =
    (mutation.state === "pending" && mutation.availableAt <= input.now) ||
    (mutation.state === "sending" &&
      mutation.claimExpiresAt !== null &&
      mutation.claimExpiresAt <= input.now);
  if (!claimable || !isNonEmptyString(input.claimId, 512)) {
    throw new TaskCommandOutboxError(
      "OUTBOX_CLAIM_LOST",
      "The task mutation is not available for this sender claim.",
    );
  }
  return decodeTaskCommandMutation({
    ...mutation,
    state: "sending",
    attemptCount: mutation.attemptCount + 1,
    retryAttemptCount: mutation.retryAttemptCount + 1,
    claimId: input.claimId,
    claimExpiresAt: input.now + input.leaseDurationMs,
    updatedAt: input.now,
  });
}

export function requeueTaskCommandMutation(
  mutation: TaskCommandMutation,
  input: {
    claimId: string;
    now: number;
    retryAt: number;
    maxRetryAttempts: number;
    errorCode: string;
    errorMessage: string;
  },
): TaskCommandMutation {
  assertClaimOwner(mutation, input.claimId);
  assertPositiveInteger(input.maxRetryAttempts, "maxRetryAttempts");
  const retryable = mutation.retryAttemptCount < input.maxRetryAttempts;
  return decodeTaskCommandMutation({
    ...mutation,
    state: retryable ? "pending" : "failed",
    availableAt: input.retryAt,
    claimId: null,
    claimExpiresAt: null,
    lastError: {
      code: input.errorCode,
      message: input.errorMessage,
      retryable,
      recordedAt: input.now,
    },
    updatedAt: input.now,
  });
}

export function markTaskCommandConflict(
  mutation: TaskCommandMutation,
  input: { claimId: string; now: number; errorCode: string; serverVersion: number | null },
): TaskCommandMutation {
  assertClaimOwner(mutation, input.claimId);
  return decodeTaskCommandMutation({
    ...mutation,
    state: "conflict",
    claimId: null,
    claimExpiresAt: null,
    lastError: {
      code: input.errorCode,
      message: "The server rejected the expected task version.",
      retryable: false,
      recordedAt: input.now,
    },
    conflict: {
      code: input.errorCode,
      serverVersion: input.serverVersion,
      recordedAt: input.now,
    },
    updatedAt: input.now,
  });
}

export function markTaskCommandFailed(
  mutation: TaskCommandMutation,
  input: { claimId: string; now: number; errorCode: string; errorMessage: string },
): TaskCommandMutation {
  assertClaimOwner(mutation, input.claimId);
  return decodeTaskCommandMutation({
    ...mutation,
    state: "failed",
    claimId: null,
    claimExpiresAt: null,
    lastError: {
      code: input.errorCode,
      message: input.errorMessage,
      retryable: false,
      recordedAt: input.now,
    },
    updatedAt: input.now,
  });
}

export function retryFailedTaskCommandMutation(
  mutation: TaskCommandMutation,
  input: { now: number; retryAt?: number },
): TaskCommandMutation {
  if (mutation.state !== "failed") {
    throw new TaskCommandOutboxError(
      "OUTBOX_INPUT_INVALID",
      "Only a failed task mutation can be retried manually.",
    );
  }
  return decodeTaskCommandMutation({
    ...mutation,
    state: "pending",
    retryAttemptCount: 0,
    availableAt: input.retryAt ?? input.now,
    claimId: null,
    claimExpiresAt: null,
    lastError:
      mutation.lastError === null
        ? null
        : { ...mutation.lastError, retryable: true, recordedAt: input.now },
    updatedAt: input.now,
  });
}

function transactionError(transaction: IDBTransaction): Error {
  return transaction.error ?? new Error("The IndexedDB transaction was aborted.");
}

function openDatabase(factory: IDBFactory, databaseName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(databaseName, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(MUTATION_STORE)) {
        const mutations = database.createObjectStore(MUTATION_STORE, { keyPath: "mutationId" });
        mutations.createIndex("workspace-created", ["workspaceId", "createdAt"]);
        mutations.createIndex("state-available", ["state", "availableAt"]);
        mutations.createIndex("idempotency-key", "idempotencyKey", { unique: true });
      }
      if (!database.objectStoreNames.contains(RECEIPT_STORE)) {
        database.createObjectStore(RECEIPT_STORE, { keyPath: "mutationId" });
      }
    };
    request.onerror = () => {
      reject(
        new TaskCommandOutboxError(
          "OUTBOX_OPEN_FAILED",
          request.error?.message ?? "The task outbox could not be opened.",
        ),
      );
    };
    request.onsuccess = () => {
      const database = request.result;
      database.onversionchange = () => database.close();
      resolve(database);
    };
  });
}

export class TaskCommandOutbox {
  readonly databaseName: string;
  private readonly database: IDBDatabase;
  private readonly leaseDurationMs: number;
  private readonly maxRetryAttempts: number;
  private readonly clock: () => number;
  private readonly uuid: () => string;

  constructor(
    database: IDBDatabase,
    options: {
      databaseName: string;
      leaseDurationMs: number;
      maxRetryAttempts: number;
      now: () => number;
      randomUUID: () => string;
    },
  ) {
    this.database = database;
    this.databaseName = options.databaseName;
    this.leaseDurationMs = options.leaseDurationMs;
    this.maxRetryAttempts = options.maxRetryAttempts;
    this.clock = options.now;
    this.uuid = options.randomUUID;
  }

  close(): void {
    this.database.close();
  }

  enqueue(input: EnqueueTaskCommandInput): Promise<TaskCommandMutation> {
    const candidate = createTaskCommandMutation(input, {
      now: this.clock(),
      randomUUID: this.uuid,
    });
    return new Promise((resolve, reject) => {
      const transaction = this.database.transaction(MUTATION_STORE, "readwrite");
      const store = transaction.objectStore(MUTATION_STORE);
      const getRequest = store.get(candidate.mutationId);
      let result = candidate;
      let failure: Error | undefined;
      getRequest.onsuccess = () => {
        try {
          if (getRequest.result === undefined) {
            store.add(candidate);
            return;
          }
          const existing = decodeTaskCommandMutation(getRequest.result);
          if (!hasSameTaskCommandIntent(existing, candidate)) {
            throw new TaskCommandOutboxError(
              "OUTBOX_MUTATION_CONFLICT",
              "This mutation ID is already bound to a different task command.",
            );
          }
          result = existing;
        } catch (error) {
          failure = asError(error);
          transaction.abort();
        }
      };
      transaction.oncomplete = () => resolve(result);
      transaction.onerror = () => reject(failure ?? transactionError(transaction));
      transaction.onabort = () => reject(failure ?? transactionError(transaction));
    });
  }

  get(mutationId: string): Promise<TaskCommandMutation | null> {
    return new Promise((resolve, reject) => {
      const transaction = this.database.transaction(MUTATION_STORE, "readonly");
      const request = transaction.objectStore(MUTATION_STORE).get(mutationId);
      request.onsuccess = () => {
        try {
          resolve(request.result === undefined ? null : decodeTaskCommandMutation(request.result));
        } catch (error) {
          reject(asError(error));
        }
      };
      request.onerror = () =>
        reject(request.error ?? new Error("The outbox row could not be read."));
    });
  }

  list(
    input: { workspaceId?: string; state?: TaskCommandOutboxState } = {},
  ): Promise<TaskCommandMutation[]> {
    return new Promise((resolve, reject) => {
      const transaction = this.database.transaction(MUTATION_STORE, "readonly");
      const request = transaction.objectStore(MUTATION_STORE).getAll();
      request.onsuccess = () => {
        try {
          const mutations = request.result
            .map(decodeTaskCommandMutation)
            .filter(
              (mutation) =>
                (input.workspaceId === undefined || mutation.workspaceId === input.workspaceId) &&
                (input.state === undefined || mutation.state === input.state),
            )
            .sort(
              (first, second) =>
                first.createdAt - second.createdAt ||
                first.mutationId.localeCompare(second.mutationId),
            );
          resolve(mutations);
        } catch (error) {
          reject(asError(error));
        }
      };
      request.onerror = () => reject(request.error ?? new Error("The outbox could not be listed."));
    });
  }

  claimNext(
    now = this.clock(),
    scope?: { workspaceId: string; actorBinding: TaskCommandActorBinding },
  ): Promise<TaskCommandMutation | null> {
    return new Promise((resolve, reject) => {
      const transaction = this.database.transaction([MUTATION_STORE, RECEIPT_STORE], "readwrite");
      const mutationStore = transaction.objectStore(MUTATION_STORE);
      const mutationsRequest = mutationStore.getAll();
      const receiptsRequest = transaction.objectStore(RECEIPT_STORE).getAllKeys();
      let mutations: TaskCommandMutation[] | undefined;
      let receiptIds: Set<string> | undefined;
      let claimed: TaskCommandMutation | null = null;
      let failure: Error | undefined;

      const claimWhenReady = () => {
        if (mutations === undefined || receiptIds === undefined || failure !== undefined) return;
        try {
          const candidate = selectNextTaskCommandMutation(mutations, receiptIds, now);
          if (candidate === null) return;
          claimed = claimTaskCommandMutation(candidate, {
            claimId: this.uuid(),
            now,
            leaseDurationMs: this.leaseDurationMs,
          });
          mutationStore.put(claimed);
        } catch (error) {
          failure = asError(error);
          transaction.abort();
        }
      };

      mutationsRequest.onsuccess = () => {
        try {
          mutations = mutationsRequest.result
            .map(decodeTaskCommandMutation)
            .filter((mutation) => scope === undefined || taskCommandMatchesScope(mutation, scope));
          claimWhenReady();
        } catch (error) {
          failure = asError(error);
          transaction.abort();
        }
      };
      receiptsRequest.onsuccess = () => {
        receiptIds = new Set(
          receiptsRequest.result.filter((key): key is string => typeof key === "string"),
        );
        claimWhenReady();
      };
      transaction.oncomplete = () => resolve(claimed);
      transaction.onerror = () => reject(failure ?? transactionError(transaction));
      transaction.onabort = () => reject(failure ?? transactionError(transaction));
    });
  }

  requeueClaim(
    mutationId: string,
    claimId: string,
    input: { retryAt: number; errorCode: string; errorMessage: string; now?: number },
  ): Promise<TaskCommandMutation> {
    const now = input.now ?? this.clock();
    return this.updateClaimed(mutationId, (mutation) =>
      requeueTaskCommandMutation(mutation, {
        claimId,
        now,
        retryAt: input.retryAt,
        maxRetryAttempts: this.maxRetryAttempts,
        errorCode: input.errorCode,
        errorMessage: input.errorMessage,
      }),
    );
  }

  markConflict(
    mutationId: string,
    claimId: string,
    input: { errorCode: string; serverVersion: number | null; now?: number },
  ): Promise<TaskCommandMutation> {
    const now = input.now ?? this.clock();
    return this.updateClaimed(mutationId, (mutation) =>
      markTaskCommandConflict(mutation, {
        claimId,
        now,
        errorCode: input.errorCode,
        serverVersion: input.serverVersion,
      }),
    );
  }

  markFailed(
    mutationId: string,
    claimId: string,
    input: { errorCode: string; errorMessage: string; now?: number },
  ): Promise<TaskCommandMutation> {
    const now = input.now ?? this.clock();
    return this.updateClaimed(mutationId, (mutation) =>
      markTaskCommandFailed(mutation, {
        claimId,
        now,
        errorCode: input.errorCode,
        errorMessage: input.errorMessage,
      }),
    );
  }

  retryFailed(
    mutationId: string,
    input: { retryAt?: number; now?: number } = {},
  ): Promise<TaskCommandMutation> {
    const now = input.now ?? this.clock();
    return this.update(mutationId, (mutation) =>
      retryFailedTaskCommandMutation(mutation, {
        now,
        ...(input.retryAt === undefined ? {} : { retryAt: input.retryAt }),
      }),
    );
  }

  acknowledgeSuccess(
    mutationId: string,
    claimId: string,
    acknowledgedAt = this.clock(),
  ): Promise<TaskCommandSuccessReceipt> {
    return new Promise((resolve, reject) => {
      const transaction = this.database.transaction([MUTATION_STORE, RECEIPT_STORE], "readwrite");
      const mutationStore = transaction.objectStore(MUTATION_STORE);
      const request = mutationStore.get(mutationId);
      let receipt: TaskCommandSuccessReceipt | undefined;
      let failure: Error | undefined;
      request.onsuccess = () => {
        try {
          if (request.result === undefined) {
            throw new TaskCommandOutboxError(
              "OUTBOX_CLAIM_LOST",
              "The claimed mutation is missing.",
            );
          }
          const mutation = decodeTaskCommandMutation(request.result);
          assertClaimOwner(mutation, claimId);
          receipt = {
            schemaVersion: 1,
            mutationId: mutation.mutationId,
            idempotencyKey: mutation.idempotencyKey,
            workspaceId: mutation.workspaceId,
            acknowledgedAt,
          };
          transaction.objectStore(RECEIPT_STORE).put(receipt);
          mutationStore.delete(mutationId);
        } catch (error) {
          failure = asError(error);
          transaction.abort();
        }
      };
      transaction.oncomplete = () => {
        if (receipt === undefined) {
          reject(new TaskCommandOutboxError("OUTBOX_CLAIM_LOST", "No success was acknowledged."));
          return;
        }
        resolve(receipt);
      };
      transaction.onerror = () => reject(failure ?? transactionError(transaction));
      transaction.onabort = () => reject(failure ?? transactionError(transaction));
    });
  }

  hasAcknowledged(mutationId: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const transaction = this.database.transaction(RECEIPT_STORE, "readonly");
      const request = transaction.objectStore(RECEIPT_STORE).get(mutationId);
      request.onsuccess = () => {
        try {
          if (request.result === undefined) {
            resolve(false);
            return;
          }
          decodeSuccessReceipt(request.result);
          resolve(true);
        } catch (error) {
          reject(asError(error));
        }
      };
      request.onerror = () =>
        reject(request.error ?? new Error("The outbox receipt could not be read."));
    });
  }

  private updateClaimed(
    mutationId: string,
    transition: (mutation: TaskCommandMutation) => TaskCommandMutation,
  ): Promise<TaskCommandMutation> {
    return this.update(mutationId, transition);
  }

  private update(
    mutationId: string,
    transition: (mutation: TaskCommandMutation) => TaskCommandMutation,
  ): Promise<TaskCommandMutation> {
    return new Promise((resolve, reject) => {
      const transaction = this.database.transaction(MUTATION_STORE, "readwrite");
      const store = transaction.objectStore(MUTATION_STORE);
      const request = store.get(mutationId);
      let result: TaskCommandMutation | undefined;
      let failure: Error | undefined;
      request.onsuccess = () => {
        try {
          if (request.result === undefined) {
            throw new TaskCommandOutboxError("OUTBOX_CLAIM_LOST", "The task mutation is missing.");
          }
          result = transition(decodeTaskCommandMutation(request.result));
          store.put(result);
        } catch (error) {
          failure = asError(error);
          transaction.abort();
        }
      };
      transaction.oncomplete = () => {
        if (result === undefined) {
          reject(new TaskCommandOutboxError("OUTBOX_CLAIM_LOST", "The mutation was not updated."));
          return;
        }
        resolve(result);
      };
      transaction.onerror = () => reject(failure ?? transactionError(transaction));
      transaction.onabort = () => reject(failure ?? transactionError(transaction));
    });
  }
}

export async function openTaskCommandOutbox(
  options: TaskCommandOutboxOptions = {},
): Promise<TaskCommandOutbox> {
  const globalFactory: unknown = Reflect.get(globalThis, "indexedDB");
  const factory =
    options.indexedDB ?? (isIndexedDbFactory(globalFactory) ? globalFactory : undefined);
  if (factory === undefined) {
    throw new TaskCommandOutboxError(
      "INDEXEDDB_UNAVAILABLE",
      "IndexedDB is unavailable in this browser context.",
    );
  }
  const databaseName = options.databaseName ?? TASK_COMMAND_OUTBOX_DATABASE;
  const leaseDurationMs = options.leaseDurationMs ?? DEFAULT_LEASE_DURATION_MS;
  const maxRetryAttempts = options.maxRetryAttempts ?? DEFAULT_MAX_RETRY_ATTEMPTS;
  assertPositiveInteger(leaseDurationMs, "leaseDurationMs");
  assertPositiveInteger(maxRetryAttempts, "maxRetryAttempts");
  const database = await openDatabase(factory, databaseName);
  return new TaskCommandOutbox(database, {
    databaseName,
    leaseDurationMs,
    maxRetryAttempts,
    now: options.now ?? Date.now,
    randomUUID: options.randomUUID ?? (() => crypto.randomUUID()),
  });
}
